import API_BASE from '../../utils/api';
import { formatLocalDate } from '../../utils/date';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import UpcomingEventsScroller from '../../components/UpcomingEventsScroller';
import { useLicense } from '../../context/LicenseContext';
import { SkeletonStatCard, SkeletonBlock } from '../../components/Skeleton';

const TeacherDashboard = () => {
  const { user } = useAuth();
  const { isModuleEnabled } = useLicense();
  const navigate = useNavigate();
  const [classInfo, setClassInfo] = useState(null);
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [stats, setStats] = useState({
    totalStudents: 0,
    presentToday: 0,
    absentToday: 0,
    attendancePercentage: 0
  });
  const [classLoading, setClassLoading] = useState(true);
  const [leavesLoading, setLeavesLoading] = useState(true);

  useEffect(() => {
    fetchClassData();
    fetchLeaves();
  }, []);

  const fetchClassData = async () => {
    try {
      const classResponse = await axios.get(`${API_BASE}/classes/my-class`);
      setClassInfo(classResponse.data);

      const today = formatLocalDate();
      const attendanceResponse = await axios.get(
        `${API_BASE}/attendance/class/${classResponse.data._id}?date=${today}`
      );
      setTodayAttendance(attendanceResponse.data);

      // Calculate stats
      const total = classResponse.data.students?.length || 0;
      const present = attendanceResponse.data.filter(a => a.status === 'present').length;
      const absent = attendanceResponse.data.filter(a => a.status === 'absent').length;

      setStats({
        totalStudents: total,
        presentToday: present,
        absentToday: absent,
        attendancePercentage: total > 0 ? Math.round((present / total) * 100) : 0
      });
    } catch (error) {
      console.error('Error fetching class data:', error);
    } finally {
      setClassLoading(false);
    }
  };

  const fetchLeaves = async () => {
    try {
      const response = await axios.get(`${API_BASE}/leaves/my-leaves?status=pending`);
      setPendingLeaves(response.data);
    } catch (error) {
      console.error('Error fetching leaves:', error);
    } finally {
      setLeavesLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">Teacher Dashboard</h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6">
          <UpcomingEventsScroller />
          {/* Class Information Card */}
          {classLoading ? (
            <div className="mb-6"><SkeletonBlock rows={1} /></div>
          ) : classInfo && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4">My Class Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Class</p>
                  <p className="font-medium">{classInfo.className} - Section {classInfo.section}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Students</p>
                  <p className="font-medium">{stats.totalStudents}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Academic Year</p>
                  <p className="font-medium">{classInfo.academicYear}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Subjects</p>
                  <p className="font-medium">{classInfo.subjects?.length || 0}</p>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { module: 'attendance', path: '/teacher/attendance', icon: '📋', title: 'Mark Attendance', desc: "Today's attendance" },
              { module: null, path: '/teacher/students', icon: '👥', title: 'Students', desc: 'View all students' },
              { module: 'leaves', path: '/teacher/leaves', icon: '📅', title: 'Leave', desc: 'Apply for leave' },
              { module: 'classes', path: '/teacher/subjects', icon: '📚', title: 'Subjects', desc: 'Teaching subjects' },
            ].filter(item => isModuleEnabled(item.module)).map(item => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="p-4 bg-white rounded-lg shadow hover:shadow-lg transition flex items-center space-x-3"
              >
                <span className="text-3xl">{item.icon}</span>
                <div className="text-left">
                  <h3 className="font-semibold">{item.title}</h3>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {classLoading ? <SkeletonStatCard /> : (
              <div className="bg-white rounded-lg shadow p-6">
                <p className="text-sm text-gray-500">Today's Attendance</p>
                <p className="text-3xl font-bold text-indigo-600">{stats.attendancePercentage}%</p>
                <p className="text-sm text-gray-500 mt-2">
                  {stats.presentToday} Present / {stats.absentToday} Absent
                </p>
              </div>
            )}
            {leavesLoading ? <SkeletonStatCard /> : (
              <div className="bg-white rounded-lg shadow p-6">
                <p className="text-sm text-gray-500">Pending Leaves</p>
                <p className="text-3xl font-bold text-yellow-600">{pendingLeaves.length}</p>
                <p className="text-sm text-gray-500 mt-2">Awaiting approval</p>
              </div>
            )}
            {classLoading ? <SkeletonStatCard /> : (
              <div className="bg-white rounded-lg shadow p-6">
                <p className="text-sm text-gray-500">Total Students</p>
                <p className="text-3xl font-bold text-green-600">{stats.totalStudents}</p>
                <p className="text-sm text-gray-500 mt-2">In your class</p>
              </div>
            )}
          </div>

          {/* Recent Activity and Schedule */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Today's Schedule */}
            {classLoading ? <SkeletonBlock rows={3} /> : (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4">Today's Schedule</h2>
                <div className="space-y-3">
                  {classInfo?.subjects?.map((subject, index) => (
                    <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                      <div className="w-16 text-sm font-medium text-gray-600">
                        {9 + index}:00 AM
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{subject.subjectName}</p>
                      </div>
                      <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded">
                        Period {index + 1}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Attendance Summary */}
            {classLoading ? <SkeletonBlock rows={3} /> : (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Recent Attendance</h2>
              {todayAttendance.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm font-medium text-gray-500 pb-2 border-b">
                    <span>Status</span>
                    <span>Count</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-600">Present</span>
                    <span className="font-medium">{stats.presentToday}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-red-600">Absent</span>
                    <span className="font-medium">{stats.absentToday}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-yellow-600">Late</span>
                    <span className="font-medium">
                      {todayAttendance.filter(a => a.status === 'late').length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-600">Half Day</span>
                    <span className="font-medium">
                      {todayAttendance.filter(a => a.status === 'half-day').length}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">
                  No attendance marked for today
                </p>
              )}
              {isModuleEnabled('attendance') && (
                <button
                  onClick={() => navigate('/teacher/attendance')}
                  className="mt-4 w-full bg-indigo-50 text-indigo-600 px-4 py-2 rounded hover:bg-indigo-100 transition"
                >
                  Mark Attendance
                </button>
              )}
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboard;